/**
 * useVulnerabilityColumns Hook
 *
 * Manages column configuration for the vulnerabilities table including:
 * - Column visibility management
 * - Smart column positioning (status first, dates last)
 * - Column ordering with drag-to-reorder support
 * - Column persistence via localStorage
 * - Default column configuration
 *
 * This hook wraps useSmartColumnPositioning with vulnerability-specific configuration.
 *
 * Positioning behavior:
 * - Status column returns to first position when toggled on
 * - Origin/Source column returns to 3rd-to-last position (before dates) when toggled on
 * - Date columns (created, visited) return to last positions when toggled on
 * - Security metrics (priority, cvss, epss, kev, exploit) insert after name when toggled on
 * - User drag-to-reorder preferences are preserved
 */

import { useCallback, useMemo } from 'react';

import { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import { Column } from '@/components/table/types';
import { RiskWithVulnerability } from '@/hooks/useRisks';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';
import {
  VULNERABILITY_COLUMN_WIDTHS,
  VULNERABILITY_TABLE_CONFIG,
} from '@/sections/vulnerabilities/config/vulnerabilityColumns.config';

/**
 * Hook parameters
 */
export interface UseVulnerabilityColumnsParams {
  /** Array of column definitions */
  columns: Column<RiskWithVulnerability>[];
  /** Default active column IDs */
  defaultActiveColumns?: string[];
  /** Storage key override (defaults to VULNERABILITY_TABLE_CONFIG.storageKey) */
  storageKey?: string;
}

/**
 * Hook return value
 */
export interface UseVulnerabilityColumnsReturn {
  /** Currently active column IDs */
  activeColumns: string[] | undefined;
  /** Set active columns */
  setActiveColumns: (columns: string[] | undefined) => void;
  /** Filtered and ordered columns based on active selection */
  columns: Column<RiskWithVulnerability>[];
  /** Sortable column configuration for Table component */
  sortableConfig: SortableColumnsConfig;
  /** Reset columns to default configuration */
  resetToDefaults: () => void;
  /** Check if a column is active */
  isColumnActive: (columnId: string) => boolean;
  /** Get column by ID */
  getColumn: (columnId: string) => Column<RiskWithVulnerability> | undefined;
  /** Get all available column IDs */
  availableColumnIds: string[];
}

/**
 * Creates the default sortable columns configuration.
 *
 * @param columns - Array of column definitions
 * @param defaultActiveColumns - Default active column IDs
 * @returns Sortable columns configuration
 */
function createDefaultConfig(
  columns: Column<RiskWithVulnerability>[],
  defaultActiveColumns: string[]
): SortableColumnsConfig {
  return {
    fixed: [],
    active: columns
      .filter(column => defaultActiveColumns.includes(column.id))
      .map(({ id, label }) => ({
        id,
        label,
        disabled: id === 'name',
        isDefault: id === 'name',
      })),
    available: columns
      .filter(column => !defaultActiveColumns.includes(column.id))
      .map(({ id, label }) => ({
        id,
        label,
      })),
  };
}

/**
 * Hook for managing vulnerability table column configuration.
 *
 * @param params - Hook configuration parameters
 * @returns Column state and helper functions
 *
 * @example
 * ```tsx
 * const columnDefs = [
 *   { id: 'status', label: 'Status', ... },
 *   { id: 'name', label: 'Name', ... },
 *   // ... more columns
 * ];
 *
 * const columnManager = useVulnerabilityColumns({
 *   columns: columnDefs,
 *   defaultActiveColumns: ['status', 'name', 'severity']
 * });
 *
 * // Use in Table component
 * <Table
 *   columns={columnManager.columns}
 *   sortableColumns={{
 *     activeColumns: columnManager.activeColumns,
 *     setActiveColumns: columnManager.setActiveColumns,
 *     defaultConfig: columnManager.sortableConfig
 *   }}
 * />
 * ```
 */
export function useVulnerabilityColumns({
  columns: columnDefs,
  defaultActiveColumns = VULNERABILITY_TABLE_CONFIG.defaultColumnOrder as unknown as string[],
  storageKey = VULNERABILITY_TABLE_CONFIG.storageKey,
}: UseVulnerabilityColumnsParams): UseVulnerabilityColumnsReturn {
  // Create sortable config
  const sortableConfig = useMemo(
    () => createDefaultConfig(columnDefs, defaultActiveColumns),
    [columnDefs, defaultActiveColumns]
  );

  // Use the smart column positioning hook with vulnerability-specific rules
  const {
    activeColumns,
    setActiveColumns: setActiveColumnsInternal,
    columns: sortedColumns,
  } = useSmartColumnPositioning({
    key: storageKey,
    defaultColumns: columnDefs,
    defaultConfig: sortableConfig,
    positioningRules: {
      firstColumns: ['status'], // Status → first when toggled on
      beforeLastColumns: ['origins', 'ticket'], // Source/Origin and Ticket (before dates) when toggled on
      lastColumns: ['created', 'visited'], // Date columns → last when toggled on
      afterColumn: {
        ids: ['priority', 'guid', 'cvss', 'epss', 'kev'], // Security metrics
        after: 'name', // Insert after name column
      },
    },
  });

  // Wrap setActiveColumns to match interface signature (string[] | undefined)
  const setActiveColumns = useCallback(
    (columns: string[] | undefined) => {
      if (columns) {
        setActiveColumnsInternal(columns);
      }
    },
    [setActiveColumnsInternal]
  );

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setActiveColumns(defaultActiveColumns);
  }, [setActiveColumns, defaultActiveColumns]);

  // Check if column is active
  const isColumnActive = useCallback(
    (columnId: string): boolean => {
      return activeColumns?.includes(columnId) ?? false;
    },
    [activeColumns]
  );

  // Get column by ID
  const getColumn = useCallback(
    (columnId: string): Column<RiskWithVulnerability> | undefined => {
      return columnDefs.find(col => col.id === columnId);
    },
    [columnDefs]
  );

  // Get all available column IDs
  const availableColumnIds = useMemo(() => {
    return columnDefs.map(col => col.id);
  }, [columnDefs]);

  return {
    activeColumns,
    setActiveColumns,
    columns: sortedColumns,
    sortableConfig,
    resetToDefaults,
    isColumnActive,
    getColumn,
    availableColumnIds,
  };
}

/**
 * Gets the column width for a specific column ID.
 *
 * @param columnId - The column ID
 * @returns The fixed width for the column or undefined
 *
 * @example
 * ```tsx
 * const width = getVulnerabilityColumnWidth('status'); // 140
 * ```
 */
export function getVulnerabilityColumnWidth(
  columnId: string
): number | undefined {
  return VULNERABILITY_COLUMN_WIDTHS[
    columnId as keyof typeof VULNERABILITY_COLUMN_WIDTHS
  ];
}

/**
 * Gets the default row height for the vulnerability table.
 *
 * @returns The default row height in pixels
 */
export function getVulnerabilityTableRowHeight(): number {
  return VULNERABILITY_TABLE_CONFIG.defaultRowHeight;
}

/**
 * Gets the storage key for vulnerability columns.
 *
 * @returns The storage key
 */
export function getVulnerabilityColumnsStorageKey(): string {
  return VULNERABILITY_TABLE_CONFIG.storageKey;
}
